import { useMemo } from "react";
import { Timestamp } from "firebase/firestore";
import type { ReportDocument, ReportEntryDocument } from "./types";
import { chunkRowsByConfig, getFillerRowCount, type FixedRowPaginationConfig } from "./fixedRowPagination";

type ReportCellValue = string | Timestamp | null | undefined;

type CashStatementRow = {
  rawEntry: Array<string | Timestamp | null>;
  currency: string;
  date: string;
  refNo: string;
  transactionType: string;
  securityCode: string;
  securityName: string;
  quantity: string;
  price: string;
  debit: string;
  credit: string;
  balance: string;
};

type CashStatementCurrencyGroup = {
  currency: string;
  rows: CashStatementRow[];
  openingBalance: string;
  totalLocalNumeric: number | null;
  totalLocal: string;
  totalBookNumeric: number | null;
  totalBook: string;
  grandTotal: string;
  showTotalBc: boolean;
  localCurrency: string;
  isFinalGroup: boolean;
};

const CASH_STATEMENT_PAGINATION: FixedRowPaginationConfig = {
  firstPageRows: 33,
  continuationRows: 36,
  displayRows: {
    firstPage: 34,
    continuation: 36,
    lastPage: 31,
    singlePage: 29,
  },
};

const currencyCodeMap: Record<string, string> = {
  "Kuwaiti Dinar": "KWD",
  "US Dollar": "USD",
  "Saudi Riyal": "SAR",
};

const parseNumericValue = (value: ReportCellValue): number | null => {
  if (typeof value !== "string" || value.trim().length === 0) {
    return null;
  }

  const parsed = Number(value.replace(/,/g, ""));
  return Number.isFinite(parsed) ? parsed : null;
};

const isTimestampValue = (value: unknown): value is Timestamp => value instanceof Timestamp;

const formatDateValue = (value: ReportCellValue): string => {
  if (!value) {
    return "";
  }

  if (isTimestampValue(value)) {
    const date = value.toDate();
    const day = `${date.getDate()}`.padStart(2, "0");
    const month = `${date.getMonth() + 1}`.padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  }

  const match = value.match(/^(\d{4})\/(\d{2})\/(\d{2})/);
  if (match) {
    const [, year, month, day] = match;
    return `${day}/${month}/${year}`;
  }

  return value;
};

const formatMoneyValue = (value: ReportCellValue): string => {
  const parsed = parseNumericValue(value);
  if (parsed === null) {
    return "";
  }

  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 3,
    maximumFractionDigits: 3,
  }).format(parsed);
};

const formatQuantityValue = (value: ReportCellValue): string => {
  const parsed = parseNumericValue(value);
  if (parsed === null) {
    return "";
  }

  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 3,
  }).format(parsed);
};

const formatPriceValue = (value: ReportCellValue): string => {
  const parsed = parseNumericValue(value);
  if (parsed === null) {
    return "";
  }

  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 3,
    maximumFractionDigits: 4,
  }).format(parsed);
};

const formatTransactionType = (value: ReportCellValue): string => {
  if (typeof value !== "string" || value.trim().length === 0) {
    return "";
  }

  if (value === "AccountingRelated") {
    return "Journal Voucher";
  }

  if (value === "Buy" || value === "Sell") {
    return `Security ${value}`;
  }

  return value;
};

const formatCurrencyLabel = (currencyValue?: string): string => {
  if (!currencyValue) {
    return "";
  }

  return currencyCodeMap[currencyValue] ?? currencyValue;
};

const inferLocalCurrencyLabel = (report: ReportDocument): string => {
  const explicitBookCurrency = report.parameters?.BookCurrency || report.parameters?.Currency;
  if (explicitBookCurrency) {
    return formatCurrencyLabel(explicitBookCurrency);
  }

  const accountingParameters = report.parameters?.AccountingParameters;
  if (accountingParameters) {
    const match = accountingParameters.match(/\b([A-Z]{3})\b(?!.*\b[A-Z]{3}\b)/);
    if (match) {
      return formatCurrencyLabel(match[1]);
    }
  }

  return "KWD";
};

const formatRateValue = (numerator: number | null, denominator: number | null): string => {
  if (
    numerator === null
    || denominator === null
    || Math.abs(denominator) < 0.0000001
  ) {
    return "";
  }

  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 6,
    maximumFractionDigits: 6,
  }).format(Math.abs(numerator / denominator));
};

const buildColumnIndex = (columns: string[]): Record<string, number> => {
  return columns.reduce<Record<string, number>>((indexMap, column, index) => {
    indexMap[column] = index;
    return indexMap;
  }, {});
};

const getCellValue = (
  entry: Array<string | Timestamp | null>,
  columnIndex: Record<string, number>,
  columnName: string,
): ReportCellValue => {
  const index = columnIndex[columnName];
  if (index === undefined) {
    return undefined;
  }

  return entry[index];
};

const parseGenevaParameterDate = (value?: string): string => {
  if (!value) {
    return "";
  }

  const match = value.match(/^(\d{4})\/(\d{2})\/(\d{2})/);
  if (!match) {
    return value;
  }

  const [, year, month, day] = match;
  return `${day}/${month}/${year}`;
};

type CashStatementTemplateProps = {
  report: ReportDocument;
  entries: ReportEntryDocument[];
};

export default function CashStatementTemplate({
  report,
  entries,
}: CashStatementTemplateProps) {
  const columns = report.result?.columns || [];
  const columnIndex = useMemo(() => buildColumnIndex(columns), [columns]);

  const rows = useMemo<CashStatementRow[]>(() => {
    return entries.map((entryDoc) => {
      const entry = entryDoc.entry || [];
      const netLocal = parseNumericValue(getCellValue(entry, columnIndex, "NetLocal"));
      const debit = netLocal !== null && netLocal > 0 ? formatMoneyValue(String(netLocal)) : "";
      const credit = netLocal !== null && netLocal < 0 ? formatMoneyValue(String(Math.abs(netLocal))) : "";
      const securityCodeValue = getCellValue(entry, columnIndex, "UnderlyingInvestment");
      const securityNameValue = getCellValue(entry, columnIndex, "Invest");
      const currencyValue = `${getCellValue(entry, columnIndex, "Currency") ?? ""}`;

      return {
        rawEntry: entry,
        currency: currencyValue,
        date: formatDateValue(
          getCellValue(entry, columnIndex, "ADate")
          ?? getCellValue(entry, columnIndex, "SDate")
          ?? getCellValue(entry, columnIndex, "TDate"),
        ),
        refNo: `${getCellValue(entry, columnIndex, "TransID") ?? ""}`,
        transactionType: formatTransactionType(getCellValue(entry, columnIndex, "EventDesc")),
        securityCode: typeof securityCodeValue === "string" ? securityCodeValue : "",
        securityName: typeof securityNameValue === "string" ? securityNameValue : "",
        quantity: formatQuantityValue(getCellValue(entry, columnIndex, "Qty")),
        price: formatPriceValue(getCellValue(entry, columnIndex, "Price")),
        debit,
        credit,
        balance: formatMoneyValue(getCellValue(entry, columnIndex, "RunCashLocal")),
      };
    }).filter((row) => {
      const hasMeaningfulTransactionData = Boolean(
        row.refNo
        || row.transactionType
        || row.securityCode
        || row.securityName
        || row.quantity
        || row.price
        || row.debit
        || row.credit,
      );

      return hasMeaningfulTransactionData;
    });
  }, [columnIndex, entries]);

  const portfolioNumber = report.parameters?.Portfolio || report.portfolioId || "";
  const portfolioName = report.parameters?.PortfolioDescription || report.portfolioId || "";
  const fromDate = parseGenevaParameterDate(report.parameters?.PeriodStartDate);
  const toDate = parseGenevaParameterDate(report.parameters?.PeriodEndDate);
  const logoSrc = import.meta.env.VITE_WEB_LOGO || "/logo.png";
  const defaultLocalCurrency = inferLocalCurrencyLabel(report);

  const currencyGroups = useMemo<CashStatementCurrencyGroup[]>(() => {
    const groups: CashStatementCurrencyGroup[] = [];
    const order: string[] = [];
    const groupedRows = new Map<string, CashStatementRow[]>();

    rows.forEach((row) => {
      if (!groupedRows.has(row.currency)) {
        groupedRows.set(row.currency, []);
        order.push(row.currency);
      }

      groupedRows.get(row.currency)?.push(row);
    });

    order.forEach((currency) => {
      const currencyRows = groupedRows.get(currency) || [];
      if (currencyRows.length === 0) {
        return;
      }

      const firstRow = currencyRows[0]?.rawEntry || [];
      const lastRow = currencyRows[currencyRows.length - 1]?.rawEntry || [];
      const totalLocalNumeric = parseNumericValue(getCellValue(lastRow, columnIndex, "CurrEndBalLocal"));
      const totalBookNumeric = parseNumericValue(getCellValue(lastRow, columnIndex, "CurrEndBalBook"));
      const localCurrency = inferLocalCurrencyLabel(report);

      groups.push({
        currency,
        rows: currencyRows,
        openingBalance: formatMoneyValue(getCellValue(firstRow, columnIndex, "CurrBegBalLocal")),
        totalLocalNumeric,
        totalLocal: formatMoneyValue(getCellValue(lastRow, columnIndex, "CurrEndBalLocal")),
        totalBookNumeric,
        totalBook: formatMoneyValue(getCellValue(lastRow, columnIndex, "CurrEndBalBook")),
        grandTotal: formatMoneyValue(getCellValue(lastRow, columnIndex, "TotalCashBook")),
        showTotalBc: Boolean(
          totalLocalNumeric !== null
          && totalBookNumeric !== null
          && Math.abs(totalLocalNumeric - totalBookNumeric) > 0.0004
        ),
        localCurrency,
        isFinalGroup: false,
      });
    });

    if (groups.length > 0) {
      groups[groups.length - 1].isFinalGroup = true;
    }

    return groups;
  }, [columnIndex, report, rows]);

  const pages = useMemo(() => {
    return currencyGroups.flatMap((group) => {
      const groupPages = chunkRowsByConfig(group.rows, CASH_STATEMENT_PAGINATION);
      return groupPages.map((page) => ({
        ...group,
        rows: page.rows,
        isFirstPage: page.isFirstPage,
        isLastPage: page.isLastPage,
      }));
    });
  }, [currencyGroups]);

  return (
    <div className="report-print-stack">
      {pages.map((page, pageIndex) => (
        (() => {
          const fillerRowCount = page.isLastPage ? 0 : getFillerRowCount(
            page.rows.length,
            page.isFirstPage ? 1 : 0,
            CASH_STATEMENT_PAGINATION,
            page.isFirstPage,
            page.isLastPage,
          );
          const currencyLabel = formatCurrencyLabel(page.currency);
          const localCurrencyLabel = formatCurrencyLabel(page.localCurrency || defaultLocalCurrency);
          const showSplitTotals = page.showTotalBc;
          const totalLabel = showSplitTotals ? `TOTAL IN ${currencyLabel}` : "TOTAL";
          const totalBookLabel = `TOTAL IN ${localCurrencyLabel}`;
          const totalBookRate = formatRateValue(page.totalBookNumeric, page.totalLocalNumeric);

          return (
        <section className="statement-page" key={`page-${pageIndex + 1}-${page.currency}`}>
          <header className="statement-header">
            <div className="statement-header-meta">
              <div>Page {pageIndex + 1} of {pages.length}</div>
              <div><strong>Portfolio No.</strong> : {portfolioNumber}</div>
              <div><strong>Portfolio Name</strong> : {portfolioName}</div>
            </div>

            <div className="statement-header-center">
              <div className="statement-title">Portfolio Statement</div>
              <div>
                <strong>From Date</strong> : {fromDate}
                <span style={{ display: "inline-block", width: "1rem" }} />
                <strong>To Date</strong> : {toDate}
              </div>
            </div>

            <div className="statement-header-right">
              <img className="statement-logo" src={logoSrc} alt="KFH Capital" />
              <div className="statement-currency">
                Currency : {currencyLabel}
              </div>
            </div>
          </header>

          <table className="statement-table statement-cash-table">
            <colgroup>
              <col style={{ width: "8%" }} />
              <col style={{ width: "5%" }} />
              <col style={{ width: "14%" }} />
              <col style={{ width: "5%" }} />
              <col style={{ width: "18%" }} />
              <col style={{ width: "7.5%" }} />
              <col style={{ width: "6%" }} />
              <col style={{ width: "9%" }} />
              <col style={{ width: "9%" }} />
              <col style={{ width: "18.5%" }} />
            </colgroup>
            <thead>
              <tr>
                <th>Date</th>
                <th>Ref. No.</th>
                <th>Transaction Type</th>
                <th colSpan={2}>Security</th>
                <th>Quantity</th>
                <th>Price</th>
                <th>Debit</th>
                <th>Credit</th>
                <th>Balance</th>
              </tr>
            </thead>
            <tbody>
              {page.isFirstPage && (
                <tr className="statement-opening-row">
                  <td className="align-left">{fromDate}</td>
                  <td />
                  <td />
                  <td colSpan={6} className="statement-opening-label">
                    Opening Balance :
                  </td>
                  <td className="align-right">{page.openingBalance}</td>
                </tr>
              )}
              {page.rows.map((row, rowIndex) => (
                <tr key={`row-${pageIndex}-${rowIndex}-${row.refNo}`}>
                  <td className="align-left"><span className="statement-cell-clip">{row.date}</span></td>
                  <td className="align-left"><span className="statement-cell-clip">{row.refNo}</span></td>
                  <td className="align-left"><span className="statement-cell-clip">{row.transactionType}</span></td>
                  <td className="align-center"><span className="statement-cell-clip">{row.securityCode}</span></td>
                  <td className="align-left"><span className="statement-cell-clip">{row.securityName}</span></td>
                  <td className="align-right"><span className="statement-cell-clip">{row.quantity}</span></td>
                  <td className="align-right"><span className="statement-cell-clip">{row.price}</span></td>
                  <td className="align-right"><span className="statement-cell-clip">{row.debit}</span></td>
                  <td className="align-right"><span className="statement-cell-clip">{row.credit}</span></td>
                  <td className="align-right"><span className="statement-cell-clip">{row.balance}</span></td>
                </tr>
              ))}
              {Array.from({ length: fillerRowCount }).map((_, fillerIndex) => (
                <tr key={`filler-${pageIndex}-${fillerIndex}`} className="statement-filler-row" aria-hidden="true">
                  <td><span className="statement-cell-clip" /></td>
                  <td><span className="statement-cell-clip" /></td>
                  <td><span className="statement-cell-clip" /></td>
                  <td><span className="statement-cell-clip" /></td>
                  <td><span className="statement-cell-clip" /></td>
                  <td><span className="statement-cell-clip" /></td>
                  <td><span className="statement-cell-clip" /></td>
                  <td><span className="statement-cell-clip" /></td>
                  <td><span className="statement-cell-clip" /></td>
                  <td><span className="statement-cell-clip" /></td>
                </tr>
              ))}
            </tbody>
          </table>

          {page.isLastPage && (
            <div className="statement-summary">
              <table className="statement-summary-table">
                <tbody>
                  <tr>
                    <td className="statement-summary-label">{totalLabel}</td>
                    <td className="statement-summary-aux" />
                    <td className="statement-summary-value">{page.totalLocal}</td>
                  </tr>
                  {showSplitTotals && (
                    <tr>
                      <td className="statement-summary-label">{totalBookLabel}</td>
                      <td className="statement-summary-aux">{totalBookRate}</td>
                      <td className="statement-summary-value">{page.totalBook}</td>
                    </tr>
                  )}
                  {page.isFinalGroup && (
                    <tr>
                      <td className="statement-summary-label">GRAND TOTAL</td>
                      <td className="statement-summary-aux" />
                      <td className="statement-summary-value">{page.grandTotal}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </section>
          );
        })()
      ))}
    </div>
  );
}
